<hawk-record-navigation
  [breadcrumbLinkList]="[
    { description: 'Lager', link: '/a/lager/uebersicht' },
    { description: 'Erwartete Lieferungen', link: '/a/lager/erwartete-lieferungen' }
  ]"
  [root]="'warehouse'"
  [leafDescription]="delivery ? delivery.order_nr : 'Laden...'"
  class="app-page"
>
  <div slot="toolbar" class="flex flex-row gap-2">
    @if (delivery && delivery.status === 'open') {
      <button class="bm-btn bm-btn-warning bm-btn-sm" (click)="showCancelConfirm = true">
        <i class="fa fa-times"></i>
        Als storniert markieren
      </button>
    }
    @if (delivery && canDelete()) {
      <button class="bm-btn bm-btn-error bm-btn-sm" (click)="showDeleteConfirm = true">
        <i class="fa fa-trash"></i>
        Löschen
      </button>
    }
  </div>

  <div class="page-content page-frame overflow-y-auto">
    @if (isLoading) {
      <div class="flex justify-center items-center h-64">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    } @else if (errorMessage) {
      <div class="alert alert-error">
        <i class="fa fa-exclamation-triangle"></i>
        <span>{{ errorMessage }}</span>
      </div>
    } @else if (delivery) {
      <div class="page-sheet max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">

        <!-- Linke Spalte: Stammdaten -->
        <div class="md:col-span-2 space-y-4">
          <!-- Header Card -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-start">
                <div>
                  <h2 class="text-xl font-bold mb-1">{{ delivery.order_nr }}</h2>
                  <p class="text-sm text-base-content/60">
                    @if (delivery.supplier_name) {
                      {{ delivery.supplier_name }}
                      <span class="mx-1">•</span>
                    }
                    Lieferung #{{ delivery.id }}
                  </p>
                </div>
                <span class="badge {{ getStatusBadgeClass(delivery.status) }}">
                  {{ getStatusLabel(delivery.status) }}
                </span>
              </div>
            </div>
          </section>

          <!-- Stammdaten -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Stammdaten</h2>
              <table class="bm-table bm-table-xs">
                <tbody>
                  <tr>
                    <td class="w-40">Lieferzeitraum</td>
                    <td>
                      @if (delivery.expected_from || delivery.expected_until) {
                        {{ delivery.expected_from | date:'dd.MM.yyyy' }}
                        @if (delivery.expected_until) {
                          - {{ delivery.expected_until | date:'dd.MM.yyyy' }}
                        }
                      } @else {
                        <span class="text-base-content/40">Nicht angegeben</span>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td>Ticket</td>
                    <td>
                      @if (delivery.ticket_id) {
                        <a
                          class="bm-link bm-link-hover text-primary"
                          [routerLink]="['/ticket', delivery.ticket_id]"
                        >
                          @if (delivery.ticket_order_nr) {
                            {{ delivery.ticket_order_nr }}
                          } @else {
                            Ticket #{{ delivery.ticket_id }}
                          }
                          <i class="fa fa-external-link ml-1"></i>
                        </a>
                      } @else {
                        <span class="text-base-content/40">-</span>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td>Projekt</td>
                    <td>{{ delivery.project_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td>Kunde</td>
                    <td>{{ delivery.customer_name || 'Eigenware' }}</td>
                  </tr>
                  <tr>
                    <td>Erwartete Lieferform</td>
                    <td>{{ getPackagingLabel(delivery.packaging_type) }}</td>
                  </tr>
                  <tr>
                    <td>Bestelldatum</td>
                    <td>{{ delivery.order_date ? (delivery.order_date | date:'dd.MM.yyyy') : '-' }}</td>
                  </tr>
                  <tr>
                    <td>Lieferschein</td>
                    <td>
                      @if (delivery.delivery_note_id) {
                        <a
                          class="bm-link bm-link-hover text-primary"
                          [routerLink]="['/lieferscheine', delivery.delivery_note_id]"
                        >
                          @if (delivery.delivery_note_number) {
                            {{ delivery.delivery_note_number }}
                          } @else {
                            Lieferschein anzeigen
                          }
                          <i class="fa fa-external-link ml-1"></i>
                        </a>
                      } @else {
                        <span class="text-base-content/40">Kein Lieferschein</span>
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          @if (delivery.notes) {
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-4">
                <h2 class="bm-card-title">Hinweise für Lageristen</h2>
                <p class="text-sm bg-warning/10 border-l-4 border-warning p-3 rounded-r">
                  {{ delivery.notes }}
                </p>
              </div>
            </section>
          }

          <!-- Artikelpositionen -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">
                Artikelpositionen
                <span class="badge badge-neutral badge-sm ml-2">{{ delivery.items_count }}</span>
              </h2>
              <div class="overflow-x-auto">
                <table class="bm-table bm-table-xs">
                  <thead>
                    <tr>
                      <th>Artikelvorlage</th>
                      <th>Artikelnr.</th>
                      <th class="text-right">Soll</th>
                      <th class="text-right">Ist</th>
                      <th class="text-right">Offen</th>
                      <th>Eigentümer</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of delivery.items; track item.id) {
                      <tr>
                        <td>{{ item.article_template_description }}</td>
                        <td class="text-base-content/60">{{ item.article_template_part_nr || '-' }}</td>
                        <td class="text-right">{{ item.quantity_ordered }}</td>
                        <td class="text-right">{{ item.quantity_received }}</td>
                        <td class="text-right font-semibold">{{ item.quantity_open }}</td>
                        <td>
                          <span class="badge badge-sm" [class.badge-primary]="item.owner === 'BENTOMAX'" [class.badge-secondary]="item.owner === 'CUSTOMER'">
                            {{ getOwnerLabel(item.owner) }}
                          </span>
                        </td>
                        <td>
                          @if (item.fully_received) {
                            <span class="badge badge-success badge-sm">Vollständig</span>
                          } @else if (item.quantity_received && item.quantity_received > 0) {
                            <span class="badge badge-warning badge-sm">Teilweise</span>
                          } @else {
                            <span class="badge badge-ghost badge-sm">Offen</span>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <!-- Wareneingänge-Historie -->
          @if (history.length > 0) {
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-4">
                <h2 class="bm-card-title">
                  Wareneingänge
                  <span class="badge badge-neutral badge-sm ml-2">{{ history.length }}</span>
                </h2>
                <div class="overflow-x-auto">
                  <table class="bm-table bm-table-xs">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Menge</th>
                        <th>Lagerplatz</th>
                        <th>Eingebucht von</th>
                        <th>Eingebucht</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (receipt of history; track receipt.id) {
                        <tr>
                          <td>{{ receipt.received_at | date:'dd.MM.yyyy HH:mm' }}</td>
                          <td>{{ receipt.quantity }}x</td>
                          <td>
                            {{ receipt.storage_bin_label }}
                            <span class="text-base-content/60">({{ receipt.warehouse_name }})</span>
                          </td>
                          <td>{{ receipt.received_by_name }}</td>
                          <td>
                            @if (receipt.article_ids.length > 0) {
                              <button
                                class="bm-link bm-link-hover text-primary"
                                (click)="showArticlesModal(receipt)"
                              >
                                {{ receipt.article_ids.length }} Artikel
                              </button>
                            } @else {
                              <span class="text-base-content/40">-</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          }
        </div>

        <!-- Rechte Spalte -->
        <div class="space-y-4">
          <!-- Fortschritt -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Fortschritt</h2>
              <div class="flex flex-col items-center py-4">
                <div class="text-3xl font-bold">{{ delivery.progress_percentage }}%</div>
                <progress
                  class="progress w-full mt-2"
                  [class.progress-success]="delivery.progress_percentage >= 100"
                  [class.progress-warning]="delivery.progress_percentage > 0 && delivery.progress_percentage < 100"
                  [class.progress-info]="delivery.progress_percentage === 0"
                  [value]="delivery.progress_percentage"
                  max="100"
                ></progress>
                <div class="text-sm text-base-content/60 mt-2">
                  {{ delivery.total_received }} / {{ delivery.total_ordered }} erhalten
                </div>
                <div class="text-sm font-semibold">
                  {{ delivery.total_open }} offen
                </div>
              </div>
            </div>
          </section>

          <!-- Sendungsverfolgung -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="bm-card-title mb-0">Sendungsverfolgung</h2>
                @if (!isEditingTracking && delivery.status !== 'cancelled') {
                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="startEditingTracking()"
                    title="Bearbeiten"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                }
              </div>

              @if (isEditingTracking) {
                <!-- Bearbeitungsmodus -->
                <div class="space-y-2">
                  @for (tracking of trackingNumbers; track $index; let i = $index) {
                    <div class="flex items-center gap-2">
                      <select class="bm-select bm-select-sm w-28" [(ngModel)]="tracking.carrier">
                        @for (carrier of carriers; track carrier) {
                          <option [value]="carrier">{{ carrier }}</option>
                        }
                      </select>
                      <input
                        class="bm-input bm-input-sm flex-1"
                        type="text"
                        [(ngModel)]="tracking.number"
                        placeholder="Tracking-Nr."
                      />
                      <button
                        class="bm-btn bm-btn-ghost bm-btn-sm text-error"
                        (click)="removeTrackingNumber(i)"
                        title="Entfernen"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  }

                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm w-full"
                    (click)="addTrackingNumber()"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Tracking-Nr. hinzufügen
                  </button>

                  <div class="flex gap-2 mt-3 pt-3 border-t border-base-200">
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                      (click)="cancelEditingTracking()"
                      [disabled]="isSavingTracking"
                    >
                      Abbrechen
                    </button>
                    <button
                      class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                      (click)="saveTrackingNumbers()"
                      [disabled]="isSavingTracking"
                    >
                      @if (isSavingTracking) {
                        <span class="loading loading-spinner loading-xs"></span>
                      }
                      Speichern
                    </button>
                  </div>
                </div>
              } @else {
                <!-- Anzeigemodus -->
                @if (getDisplayTrackingNumbers().length > 0) {
                  <div class="space-y-1">
                    @for (tracking of getDisplayTrackingNumbers(); track $index) {
                      <div class="flex items-center gap-2 text-sm">
                        <span class="badge badge-sm badge-outline">{{ tracking.carrier }}</span>
                        <span class="font-mono">{{ tracking.number }}</span>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-sm text-base-content/40">
                    Keine Tracking-Nummern hinterlegt.
                  </p>
                }
              }
            </div>
          </section>

          <!-- Benachrichtigungen -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="bm-card-title mb-0">Benachrichtigungen</h2>
                @if (!isEditingNotifications && delivery.status !== 'cancelled') {
                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="startEditingNotifications()"
                    title="Bearbeiten"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                }
              </div>

              @if (isEditingNotifications) {
                <!-- Bearbeitungsmodus -->
                <div class="space-y-3">
                  <div>
                    <label class="bm-fieldset-label">Interne E-Mails</label>
                    <input
                      class="bm-input bm-input-sm w-full"
                      type="text"
                      [(ngModel)]="editInternalEmails"
                      placeholder="email1@firma.de; email2@firma.de"
                    />
                  </div>
                  <div>
                    <label class="bm-fieldset-label">Externe E-Mails</label>
                    <input
                      class="bm-input bm-input-sm w-full"
                      type="text"
                      [(ngModel)]="editExternalEmails"
                      placeholder="kunde@example.com"
                    />
                  </div>
                  <p class="text-xs text-base-content/60">
                    Mehrere E-Mails mit Semikolon trennen.
                  </p>

                  <div class="flex gap-2 pt-2 border-t border-base-200">
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                      (click)="cancelEditingNotifications()"
                      [disabled]="isSavingNotifications"
                    >
                      Abbrechen
                    </button>
                    <button
                      class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                      (click)="saveNotifications()"
                      [disabled]="isSavingNotifications"
                    >
                      @if (isSavingNotifications) {
                        <span class="loading loading-spinner loading-xs"></span>
                      }
                      Speichern
                    </button>
                  </div>
                </div>
              } @else {
                <!-- Anzeigemodus -->
                @if (delivery.internal_notification_emails || delivery.external_notification_emails) {
                  <div class="space-y-2">
                    @if (delivery.internal_notification_emails) {
                      <div>
                        <span class="text-xs text-base-content/60 uppercase">Intern</span>
                        <div class="text-sm">
                          @for (email of delivery.internal_notification_emails.split(';'); track email) {
                            <div class="break-all">{{ email.trim() }}</div>
                          }
                        </div>
                      </div>
                    }
                    @if (delivery.external_notification_emails) {
                      <div>
                        <span class="text-xs text-base-content/60 uppercase">Extern</span>
                        <div class="text-sm">
                          @for (email of delivery.external_notification_emails.split(';'); track email) {
                            <div class="break-all">{{ email.trim() }}</div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-sm text-base-content/40">
                    Keine Benachrichtigungen konfiguriert.
                  </p>
                }
              }
            </div>
          </section>

          <!-- Meta -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Info</h2>
              <table class="bm-table bm-table-xs">
                <tbody>
                  <tr>
                    <td>Erstellt von</td>
                    <td>{{ delivery.created_by_name }}</td>
                  </tr>
                  <tr>
                    <td>Erstellt am</td>
                    <td>{{ delivery.created_at | date:'dd.MM.yyyy HH:mm' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

      </div>
    }
  </div>

  <!-- Stornieren Modal -->
  @if (showCancelConfirm) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box">
        <h3 class="text-lg font-bold">Lieferung storniert?</h3>
        <p class="py-4">
          Möchten Sie die erwartete Lieferung wirklich als storniert markieren?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="bm-modal-action">
          <button class="bm-btn bm-btn-ghost" (click)="showCancelConfirm = false">Abbrechen</button>
          <button class="bm-btn bm-btn-error" (click)="cancelDelivery()">Als storniert markieren</button>
        </div>
      </div>
    </dialog>
  }

  <!-- Löschen Modal -->
  @if (showDeleteConfirm) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box">
        <h3 class="text-lg font-bold">Lieferung löschen?</h3>
        <p class="py-4">
          Möchten Sie die erwartete Lieferung <strong>#{{ delivery?.id }}</strong> endgültig löschen?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="bm-modal-action">
          <button class="bm-btn bm-btn-ghost" (click)="showDeleteConfirm = false">Abbrechen</button>
          <button class="bm-btn bm-btn-error" (click)="deleteDelivery()">Endgültig löschen</button>
        </div>
      </div>
    </dialog>
  }

  <!-- Artikel Modal -->
  @if (selectedReceipt) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box max-w-lg">
        <h3 class="text-lg font-bold mb-4">
          Eingebuchte Artikel
          <span class="badge badge-neutral badge-sm ml-2">{{ selectedReceipt.article_ids.length }}</span>
        </h3>
        <div class="overflow-x-auto max-h-96">
          <table class="bm-table bm-table-xs">
            <thead>
              <tr>
                <th>Artikel-ID</th>
                <th>Seriennummer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (articleId of selectedReceipt.article_ids; track articleId; let i = $index) {
                <tr>
                  <td>#{{ articleId }}</td>
                  <td>
                    @if (selectedReceipt.article_serial_numbers[i]) {
                      {{ selectedReceipt.article_serial_numbers[i] }}
                    } @else {
                      <span class="text-base-content/40">-</span>
                    }
                  </td>
                  <td class="text-right">
                    <a
                      class="bm-link bm-link-hover text-primary"
                      [routerLink]="['/artikel', articleId]"
                    >
                      Details <i class="fa fa-external-link ml-1"></i>
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="bm-modal-action mt-4">
          <button class="bm-btn bm-btn-ghost" (click)="selectedReceipt = null">Schließen</button>
        </div>
      </div>
    </dialog>
  }
</hawk-record-navigation>
